import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Divider, message, Input } from "antd";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import LanguageSwitcher from "src/components/LanguageSwitcher";
import { clearLocalStorageExcept } from "src/utils/HelperFunctions";
import {
  signupUser,
  handleGoogleOAuthCallback,
  getGoogleLoginUrl,
  validateEmail,
  validatePassword,
  redirectBasedOnStatus,
} from "../../services/AuthenticationAPIs";

const SignUpContent = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  useLanguage(); // Initialize RTL support

  const [values, setValues] = useState({ email: "", password: "" });
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    redirectBasedOnStatus(navigate);
  }, [navigate]);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (code && state) {
        try {
          const data = await handleGoogleOAuthCallback(code, state);
          if (data.status) {
            message.success(data.message || t("login.googleLoginSuccessful"));
          } else {
            message.error(data.message || t("login.googleLoginFailed"));
          }
        } catch {
          message.error(
            t("login.googleLoginError")
          );
        }
      }
    };

    handleOAuthCallback();
  }, []);

  const validateInputs = (): boolean => {
    const emailErr = validateEmail(values.email);
    const passwordErr = validatePassword(values.password);

    setEmailError(emailErr);
    setPasswordError(passwordErr);

    return !emailErr && !passwordErr;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearLocalStorageExcept(["admin_token", "admin_name"]);

    if (!validateInputs()) {return;}

    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const data = await signupUser(values.email, values.password);

  if (data.status) {
  message.success(data.message || t("signup.signupSuccessful"));
  localStorage.setItem("api_key", data.api_key);
  localStorage.setItem("user_id", data.user_id);
  localStorage.setItem("tenant_id", data.tenant_id);
  localStorage.setItem("role_id", data.role_id);
  localStorage.setItem("email", data.email);
  navigate("/verify-otp" , { state: { from: "sign-up" } });
} else {
  const errorMsg =
    data.message || data.error || t("signup.signupFailed");
  message.error(errorMsg);
}
    }  catch (error: any) {
  const backendMessage =
    error?.response?.data?.message ||
    error?.message ||
    t("signup.somethingWentWrong");
  message.error(backendMessage);
} finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleGoogleLogin = () => {
    clearLocalStorageExcept(["admin_token", "admin_name"]);
    try {
      window.location.href = getGoogleLoginUrl();
    } catch {
      message.error(t("login.failedInitiateGoogle"));
    }
  };

  return (
    <div
      className="body"
      style={{ "--auth-bg-image": "url(/images/DatatramSignup.png)" } as React.CSSProperties}
    >
      <div className="logo-wrapper">
        <img src="/images/DpLogo.png" className="logo-design" alt="logo" />
        <LanguageSwitcher />
      </div>
      <div className="app">
        <div className="signup-internal-container">
          <form className="formdesign" onSubmit={handleSubmit} noValidate>
            <h2 className="signup-heading">
              {i18n.language === "ar" ? (
                t("signup.headingSingle")
              ) : (
                <>
                  {t("signup.headingLine1")}
                  <br />
                  {t("signup.headingLine2")}
                </>
              )}
            </h2>
            <br />
            <button
              onClick={handleGoogleLogin}
              className="custom-google-button"
              type="button"
            >
              <img
                src="/images/google-logo.svg"
                alt="Google logo"
              className="signup-google-logo"
              />
              {t("signup.signUpWithGoogle")}
            </button>
            <br />
            <Divider className="or">{t("signup.or")}</Divider>
            <br />
            <div className="input-container">
              <label>{t("signup.emailAddress")}</label>
              <Input
                name="email"
                className="input-email-password input-style-2"
                placeholder={t("signup.emailAddress")}
                value={values.email}
                onChange={onChange}
                required
              />
              {emailError && (
                <span className="error-message">{emailError}</span>
              )}
            </div>
            <div className="input-container">
              <label>{t("signup.createPassword")}</label>
              <Input.Password
                name="password"
                className="input-email-password input-style-2"
                placeholder={t("signup.createPassword")}
                value={values.password}
                onChange={onChange}
                required
                disabled={isLoading}
              />
              {passwordError && (
                <span className="error-message">{passwordError}</span>
              )}
            </div>
            <button
              type="submit"
              className="button-style"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("signup.creatingAccount") : t("signup.createAccount")}
            </button>
            <p className="signup-page-text alreadyRegistered_text">
              {t("signup.alreadyHaveAccount")}&nbsp;
              <a href="/log-in">{t("signup.logIn")}</a>
            </p>
            <p className="signup-page-text viewTerms_text">
              {t("signup.agreeTerms")}&nbsp;
              <a href="/privacy-policy">{t("signup.termsAndConditions")}</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

const SignUp = () => (
  <GoogleOAuthProvider clientId="996633909190-2nnsu9ssi3kmjtkct4dsvt9e7hdgn1fj.apps.googleusercontent.com">
    <SignUpContent />
  </GoogleOAuthProvider>
);

export default SignUp;
